// One toolkit (02-DESIGN/08-toolkits/01-the-toolkit-contract.md § the
// signature; 03-linking.md § the flow, § unlinking), in tabs
// (02-DESIGN/03-console/01-the-console.md § tabs; the moves, page by page):
// *Overview* — what it acts as, what it offers counted, what it needs from
// this person and the one act for it, the server it wraps and its addresses
// as quiet values with copy; *Tools* — one row per tool, which opens the
// tool's page, `toolkits/<toolkit>/tools/<tool>`, with its description; for
// an admin *Settings* and *Workload* — its state, output and events;
// *Remove* for an admin, offered twice. *Connect* is a form in the tab's
// place (§ conventions: a form; hq decision 0069). This page is also the redirect the toolkit's
// registration at the provider carries: a return from the consent screen
// lands here with a code and a state, the console exchanges the code — at
// the provider for a public client, through the toolkit for a confidential
// one — and deposits the refresh token at the person's own path on their own
// connection. Its address at the gateway is shown twice: the bare one an
// app reaches with its key, and the workspace's, which a person's own AI
// client reaches by signing them in (02-DESIGN/09-gateway/02-the-oauth-face.md
// § the address, § what the console shows).

import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { usePage } from "../../platform/session.tsx";
import {
  Button,
  Confirm,
  Empty,
  NotAllowed,
  Notice,
  PageHead,
  Pending,
  Pill,
  plural,
  useLoad,
} from "../../ui/components.tsx";
import { Ident } from "../../ui/ident.tsx";
import { useFormState } from "../../ui/form.tsx";
import { Table } from "../../ui/table.tsx";
import { Tabs, type TabSpec } from "../../ui/tabs.tsx";
import { ClientType, exchangePublic, find, linkPath, validName, type Toolkit } from "../../wire/toolkit.ts";
import { META_RUNTIME_URL } from "../../wire/toolkit-mcp.ts";
import { idSubject, SERVICE as WORKLOADS, type Workload } from "../../wire/workloads.ts";
import { appAddress, personAddress } from "../apps/desktop.ts";
import { useSurface } from "../surface.tsx";
import { EventsPane, OutputPane } from "../workloads/panes.tsx";
import { plainState } from "../workloads/state.ts";
import { WorkloadSettings } from "../configuration/WorkloadSettings.tsx";
import { ConnectRecord, ToolkitActs, whyNot } from "./Acts.tsx";
import { callbackOf, takePendingLink } from "./link.ts";
import { schemeWords, scopeWords, standingOf, standingWords } from "./state.ts";
import { removeConsequence, removeToolkit } from "./remove.ts";
import { useToolkits } from "./useToolkits.ts";

/** ADDRESS is how much of an address shows before it is shortened: most addresses show whole. */
const ADDRESS = 48;

/** firstLine is a tool's description as its row shows it: the first line, the rest on the tool's page. */
function firstLine(d: string): string {
  if (d === "") {
    return "No description.";
  }
  const line = d.split("\n")[0] ?? d;
  return line.length > 120 ? `${line.slice(0, 119)}…` : line;
}

export function ToolkitPage() {
  const { name = "" } = useParams();
  const s = useSurface();
  if (!validName(name)) {
    return (
      <PageHead
        crumb={<Link to={tenantPath(s.tenant, "toolkits")}>Toolkits</Link>}
        title="Not a toolkit"
        line={`"${name}" is not a toolkit's name.`}
      />
    );
  }
  return <One name={name} />;
}

/** ToolkitTab is one of a toolkit's tabs (01-the-console.md § conventions: the pages that have tabs). */
export type ToolkitTab = "overview" | "tools" | "settings" | "workload";

/**
 * tabForForm is the tab a toolkit's page shows: the one its open form
 * belongs to, else the one the person chose. The form lives in the history
 * entry's state and the tab does not, so a refresh, Back or Forward with a
 * form open lands on that form's tab — *Connect* on *Overview*, the
 * configuration surface's *add* on *Settings* for an admin — never on a tab
 * where the form cannot be seen.
 */
export function tabForForm(form: string | undefined, chosen: ToolkitTab, manage: boolean): ToolkitTab {
  if (form === "connect") {
    return "overview";
  }
  if (form === "add" && manage) {
    return "settings";
  }
  return chosen;
}

/** Finish is where the return from the provider stands. */
type Finish =
  | { kind: "none" }
  | { kind: "working"; step: string }
  | { kind: "linked" }
  | { kind: "failed"; err: unknown };

function One({ name }: { name: string }) {
  const s = useSurface();
  const { platform } = usePage();
  const gateway = platform.gateway_url || undefined;
  const t = useToolkits();
  const location = useLocation();
  const navigate = useNavigate();
  const [err, setErr] = useState<unknown>(undefined);
  const [finish, setFinish] = useState<Finish>({ kind: "none" });
  const [removed, setRemoved] = useState(false);
  // A link may carry the tab it came from (§ conventions: tabs): a document's
  // page opened from the Settings tab returns to it.
  const [chosen, setChosen] = useState<ToolkitTab>(
    typeof location.state === "object" &&
      location.state !== null &&
      (location.state as { tab?: unknown }).tab === "settings"
      ? "settings"
      : "overview",
  );
  const forms = useFormState<"connect" | "add">();
  const callback = useRef(callbackOf(location.search));
  const crumb = (
    <>
      <Link to={tenantPath(s.tenant, "toolkits")}>Toolkits</Link>
      <span>/</span>
      <span>{name}</span>
    </>
  );

  // The return from the provider: once, when discovery has answered and the
  // toolkit is the one the link was begun for.
  const bus = s.bus;
  const principal = s.principal;
  const toolkitsState = t.toolkits.state;
  const toolkitsValue = t.toolkits.state === "ready" ? t.toolkits.value : undefined;
  const reload = t.reload;
  useEffect(() => {
    const cb = callback.current;
    if (cb === undefined || toolkitsState !== "ready" || toolkitsValue === undefined) {
      return;
    }
    callback.current = undefined;
    const clear = () => {
      void navigate(tenantPath(s.tenant, "toolkits", name), { replace: true });
    };
    if (cb.kind === "refused") {
      setFinish({ kind: "failed", err: new Error(`The provider refused: ${cb.reason}`) });
      clear();
      return;
    }
    const pending = takePendingLink(cb.state);
    if (pending === undefined || pending.toolkit !== name) {
      setFinish({
        kind: "failed",
        err: new Error("This link was not begun in this tab, or it took too long. Link again."),
      });
      clear();
      return;
    }
    let tk: Toolkit;
    try {
      tk = find(toolkitsValue, name);
    } catch (e) {
      setFinish({ kind: "failed", err: e });
      clear();
      return;
    }
    if (tk.workload !== pending.workload) {
      setFinish({
        kind: "failed",
        err: new Error(`${name} is not the toolkit the link was begun for. Link again.`),
      });
      clear();
      return;
    }
    const x = { code: cb.code, verifier: pending.verifier, redirectUri: pending.redirectUri };
    setFinish({ kind: "working", step: "Exchanging the code" });
    const exchange =
      tk.link?.clientType === ClientType.confidential
        ? bus.exchangeConfidential(tk, x)
        : exchangePublic(tk, x);
    exchange
      .then((token) => {
        setFinish({ kind: "working", step: "Storing the credential for you" });
        return bus.vault().store(linkPath(principal, tk), token);
      })
      .then(
        () => {
          setFinish({ kind: "linked" });
          reload();
        },
        (e: unknown) => {
          setFinish({ kind: "failed", err: e });
        },
      );
    clear();
  }, [toolkitsState, toolkitsValue, name, bus, principal, navigate, s.tenant, reload]);

  if (t.toolkits.state === "pending") {
    return (
      <>
        <div className="crumb">{crumb}</div>
        <Pending what="Looking for the toolkit" />
      </>
    );
  }
  if (t.toolkits.state === "refused") {
    return (
      <>
        <PageHead crumb={crumb} title={name} />
        <NotAllowed err={t.toolkits.err} what="This toolkit" />
      </>
    );
  }
  let tk: Toolkit | undefined;
  let ambiguous = false;
  try {
    tk = find(t.toolkits.value, name);
  } catch {
    ambiguous = t.toolkits.value.filter((x) => x.name === name).length > 1;
  }
  if (tk === undefined) {
    const w =
      t.offered.state === "ready" ? t.offered.value.find((o) => o.name === name)?.workload : undefined;
    return (
      <>
        <PageHead
          crumb={crumb}
          title={name}
          line={
            ambiguous
              ? `Two toolkits in ${s.name} carry this name; neither can be used until one is renamed.`
              : w !== undefined
                ? "Not serving yet."
                : `No toolkit by this name is serving in ${s.name} right now.`
          }
        />
        {removed && <Notice kind="ok">Removed. It stops and stays listed as removed under Workloads.</Notice>}
        {finish.kind === "failed" && <NotAllowed err={finish.err} what="Link" />}
        {w !== undefined && (
          <div className="card">
            <dl className="kv">
              <dt>Status</dt>
              <dd>
                <Pill tone={plainState(w).tone}>{plainState(w).word}</Pill>
              </dd>
              <dt>Workload</dt>
              <dd>
                {s.offered("workloads:list:any") ? (
                  <Link className="text-link mono" to={tenantPath(s.tenant, "workloads", w.id)}>
                    {w.id}
                  </Link>
                ) : (
                  <span className="mono">{w.id}</span>
                )}
              </dd>
            </dl>
            <p className="small faint" style={{ marginTop: 16 }}>
              A toolkit lists its tools once it has read its settings and reached its server. Its output under
              Workloads says what it is waiting for.
            </p>
          </div>
        )}
      </>
    );
  }

  const kit = tk;
  const linked = t.linked.state === "ready" ? t.linked.value : new Set<string>();
  const standing = standingOf(tk, linked, s.principal);
  const sw = standingWords(standing);
  const blocked = whyNot(s.principal, t.noVault, s.name, t.vault);
  const server = tk.metadata[META_RUNTIME_URL];
  const workload = tk.workload;
  const manage = s.offered("toolkits:manage");
  const tab = tabForForm(forms.open, chosen, manage);
  // Choosing another tab leaves the open form, as Cancel would.
  const setTab = (t: ToolkitTab) => {
    if (forms.open !== undefined && t !== tab) {
      forms.close();
    }
    setChosen(t);
  };
  const tabs: TabSpec<ToolkitTab>[] = [
    { id: "overview", label: "Overview" },
    { id: "tools", label: "Tools" },
    ...(manage
      ? [
          { id: "settings" as const, label: "Settings" },
          { id: "workload" as const, label: "Workload" },
        ]
      : []),
  ];
  const remove = () => {
    setErr(undefined);
    removeToolkit(s.bus, workload).then(
      () => {
        setRemoved(true);
        t.reload();
      },
      (e: unknown) => {
        setErr(e);
        t.reload();
      },
    );
  };

  return (
    <>
      <PageHead
        crumb={crumb}
        title={tk.name}
        line={`${scopeWords(tk.scope, s.name)}${server === undefined ? "" : ` · wraps ${server}`}`}
        action={
          tab === "overview" && forms.open === undefined && s.offered("toolkits:link") ? (
            <ToolkitActs
              tk={tk}
              standing={standing}
              noVault={t.noVault}
              onChanged={t.reload}
              onError={setErr}
              onConnect={() => {
                forms.show("connect");
              }}
            />
          ) : undefined
        }
      />
      {finish.kind === "working" && <Pending what={finish.step} />}
      {finish.kind === "linked" && (
        <Notice kind="ok" title={`${tk.name} is linked`}>
          It acts as you from now on. Unlinking here deletes what was stored; revoking access is done in your
          provider account.
        </Notice>
      )}
      {finish.kind === "failed" && <NotAllowed err={finish.err} what="Link" />}
      {err !== undefined && <NotAllowed err={err} />}
      {blocked !== undefined && tk.scope === "person" && <Notice>{blocked}</Notice>}

      <Tabs label={tk.name} tabs={tabs} value={tab} onChange={setTab}>
        {tab === "overview" && forms.open === "connect" && (
          <ConnectRecord
            tk={kit}
            back={kit.name}
            heading="h2"
            onClose={forms.close}
            onConnected={() => {
              forms.close();
              t.reload();
            }}
          />
        )}
        {tab === "overview" && forms.open !== "connect" && (
          <>
            <section className="section card">
              <dl className="kv">
                <dt>Acts as</dt>
                <dd>{scopeWords(tk.scope, s.name)}</dd>
                <dt>Offers</dt>
                <dd>
                  {tk.tools.length === 0 ? (
                    <span className="faint">no tools listed yet</span>
                  ) : (
                    <button
                      type="button"
                      className="text-link"
                      onClick={() => {
                        setTab("tools");
                      }}
                    >
                      {`offers ${plural(tk.tools.length, "tool")}`}
                    </button>
                  )}
                </dd>
                <dt>Needs from you</dt>
                <dd>{schemeWords(tk, s.name)}</dd>
                <dt>For you</dt>
                <dd>
                  <Pill tone={sw.tone}>{sw.word}</Pill>
                </dd>
                {tk.setup !== undefined && tk.setup.instructions !== "" && (
                  <>
                    <dt>How</dt>
                    <dd>{tk.setup.instructions}</dd>
                  </>
                )}
                {server !== undefined && (
                  <>
                    <dt>Server</dt>
                    <dd>
                      <Ident value={server} label="server address" head={ADDRESS} />
                    </dd>
                  </>
                )}
                {gateway !== undefined && (
                  <>
                    <dt>Address for an app</dt>
                    <dd>
                      <Ident value={appAddress(gateway, tk.name)} label="address" head={ADDRESS} />
                    </dd>
                    <dt>Address for your AI client</dt>
                    <dd>
                      <Ident
                        value={personAddress(gateway, s.tenant, tk.name)}
                        label="address"
                        head={ADDRESS}
                      />
                    </dd>
                  </>
                )}
                <dt>Workload</dt>
                <dd>
                  <Ident
                    value={tk.workload}
                    label="id"
                    to={
                      s.offered("workloads:list:any")
                        ? tenantPath(s.tenant, "workloads", tk.workload)
                        : undefined
                    }
                  />
                </dd>
              </dl>
              {manage && !removed && (
                <div className="row" style={{ marginTop: 20, justifyContent: "flex-end" }}>
                  <Confirm label="Remove" consequence={removeConsequence(tk.name)} onConfirm={remove} />
                </div>
              )}
            </section>
          </>
        )}
        {tab === "tools" &&
          (tk.tools.length === 0 ? (
            <Empty>No tools listed yet. A toolkit lists them once it has reached its server.</Empty>
          ) : (
            <Table
              label="Tools"
              rows={tk.tools}
              rowKey={(x) => x.name}
              sort={{ key: "name", dir: "asc" }}
              to={(x) => tenantPath(s.tenant, "toolkits", kit.name, "tools", x.name)}
              columns={[
                {
                  key: "name",
                  header: "Tool",
                  value: (x) => x.name,
                  text: (x) => `${x.name} ${x.description}`,
                  cell: (x) => (
                    <span className="cell">
                      <span className="cell-main">{x.name}</span>
                      <span className="cell-sub">{firstLine(x.description)}</span>
                    </span>
                  ),
                },
              ]}
            />
          ))}
        {tab === "settings" && manage && (
          <WorkloadSettings
            workload={workload}
            canEdit={!removed}
            compact
            back={{ to: tenantPath(s.tenant, "toolkits", name), label: tk.name, tab: "settings" }}
          />
        )}
        {tab === "workload" && manage && <ToolkitWorkload id={workload} />}
      </Tabs>
      {removed && (
        <div className="row">
          <Button
            kind="quiet"
            onClick={() => {
              void navigate(tenantPath(s.tenant, "toolkits"));
            }}
          >
            Back to Toolkits
          </Button>
        </div>
      )}
    </>
  );
}

/** ToolkitWorkload is the Workload tab: the toolkit's workload, its state, output and events as a workload's. */
function ToolkitWorkload({ id }: { id: string }) {
  const s = useSurface();
  const [load] = useLoad(`toolkit-workload/${id}`, () =>
    s.bus.call<Workload>(WORKLOADS, idSubject("get", id), undefined),
  );
  if (load.state === "pending") {
    return <Pending what="Reading the workload" />;
  }
  if (load.state === "refused") {
    return <NotAllowed err={load.err} what="The workload" />;
  }
  const w = load.value;
  const p = plainState(w);
  return (
    <>
      <div className="row" style={{ marginBottom: 20 }}>
        <Pill tone={p.tone}>{p.word}</Pill>
        {w.placement.executor !== "" && <span className="small muted">on {w.placement.executor}</span>}
        <Link className="text-link small" to={tenantPath(s.tenant, "workloads", w.id)}>
          its page
        </Link>
        <Ident value={w.id} label="id" />
      </div>
      <section className="section">
        <div className="section-head">
          <h2>Output</h2>
        </div>
        <OutputPane id={id} placement={w.placement} />
      </section>
      <section className="section">
        <div className="section-head">
          <h2>Events</h2>
        </div>
        <EventsPane id={id} placement={w.placement} />
      </section>
    </>
  );
}
